import "./css/changepassword.css"
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const ChangePassword = () => {
    const navigate = useNavigate();
    const [oldPasswordVisible, setOldPasswordVisible] = useState(false);
    const [newPasswordVisible, setNewPasswordVisible] = useState(false);
    const [verifyPasswordVisible, setVerifyPasswordVisible] = useState(false);

    const [password, setPassword] = useState("");
    const [currentPassword, setCurrentPassword] = useState("");
    const [verify, setVerify] = useState("");

    // Error States
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Clear errors when typing
    useEffect(() => {
        if (password || currentPassword || verify) setErrorMessage("");
    }, [password, currentPassword, verify]);

    const validateClientSide = () => {
        if (!password.trim()) {
            setErrorMessage("Enter your old password.");
            return false;
        }
        if (!currentPassword.trim()) {
            setErrorMessage("Enter your new password.");
            return false;
        } else if (currentPassword.length < 6) {
            setErrorMessage("New password must be at least 6 characters.");
            return false;
        } else if (currentPassword.length > 25) {
            setErrorMessage("Maximum new password length is 25 characters.");
            return false;
        }
        if (!verify.trim()) {
            setErrorMessage("Confirm your new password.");
            return false;
        } else if (currentPassword !== verify) {
            setErrorMessage("New passwords do not match.");
            return false;
        }
        return true;
    };

    const handleChangePassword = async () => {
        if (!validateClientSide()) return; 

        setIsSubmitting(true);

        const data = {
            password: password,
            newPassword: currentPassword,
            verify: verify
        };

        try {
            const response = await fetch(process.env.REACT_APP_SERVER_URL + "/user/changepassword", {
                method: "POST",
                body: JSON.stringify(data),
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${localStorage.getItem("token")}`
                },
            });
            
            const result = await response.json();
            
            // Handle Session Expiry or Token Issue
            if (result.status === false && result.error === "loginError") {
                setErrorMessage("Your session has expired. Redirecting to login...");
                setTimeout(() => {
                    localStorage.clear();
                    navigate("/login");
                }, 2000);
                return;
            }

            // Handle Express Validator Errors
            if (result.error && Array.isArray(result.error)) {
                setErrorMessage(result.error[0].msg);
                return;
            }

            // Handle Incorrect Old Password or other API errors
            if (result.status === false) {
                setErrorMessage(result.message || "Failed to change password.");
                return;
            }

            // Success Case
            if (result.status === true) {
                setErrorMessage("Password changed successfully! Redirecting...");
                setTimeout(() => {
                    localStorage.clear();
                    navigate("/login");
                }, 2000);
            }

        } catch (error) {
            console.error("Change Password Error:", error);
            setErrorMessage("Server connection failed. Please try again later.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="changePasswordPageWrapper">
            <div className="changePasswordMainContainer">
                <div className="changePasswordHeader">
                    <h2>Change Password</h2>
                    <p>Update your password to keep your account secure</p>
                </div>

                <div className="changePasswordFormContent">
                    
                    {/* Display Errors Visually */}
                    {errorMessage && (
                        <div className={`formAlertMessage ${errorMessage.includes("successfully") ? 'successMessage' : 'errorMessage'}`}>
                            {errorMessage.includes("successfully") ? <i className="fa-solid fa-circle-check"></i> : <i className="fa-solid fa-circle-exclamation"></i>}
                            <span>{errorMessage}</span>
                        </div>
                    )}

                    <div className="changePasswordFormGroup">
                        <label className="changePasswordLabel">Old Password</label>
                        <div className="changePasswordInputWrapper">
                            <div className="changePasswordIcon"><i className="fa-solid fa-lock"></i></div>
                            <input type={oldPasswordVisible ? "text" : "password"}
                                className="changePasswordInputField"
                                placeholder="Enter Old Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)} 
                            />
                            <button className="changePasswordEyeBtn" onClick={() => setOldPasswordVisible(!oldPasswordVisible)}>
                                {oldPasswordVisible ? <i className="fa-solid fa-eye-slash"></i> : <i className="fa-solid fa-eye"></i>}
                            </button>
                        </div>
                    </div>

                    <div className="changePasswordFormGroup">
                        <label className="changePasswordLabel">New Password</label>
                        <div className="changePasswordInputWrapper">
                            <div className="changePasswordIcon"><i className="fa-solid fa-key"></i></div>
                            <input type={newPasswordVisible ? "text" : "password"}
                                className="changePasswordInputField"
                                placeholder="Enter New Password"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)} 
                            />
                            <button className="changePasswordEyeBtn" onClick={() => setNewPasswordVisible(!newPasswordVisible)}>
                                {newPasswordVisible ? <i className="fa-solid fa-eye-slash"></i> : <i className="fa-solid fa-eye"></i>}
                            </button>
                        </div>
                    </div>

                    <div className="changePasswordFormGroup">
                        <label className="changePasswordLabel">Verify Password</label>
                        <div className="changePasswordInputWrapper">
                            <div className="changePasswordIcon"><i className="fa-solid fa-shield-check"></i></div>
                            <input type={verifyPasswordVisible ? "text" : "password"}
                                className="changePasswordInputField"
                                placeholder="Confirm New Password"
                                value={verify}
                                onChange={(e) => setVerify(e.target.value)} 
                            />
                            <button className="changePasswordEyeBtn" onClick={() => setVerifyPasswordVisible(!verifyPasswordVisible)}>
                                {verifyPasswordVisible ? <i className="fa-solid fa-eye-slash"></i> : <i className="fa-solid fa-eye"></i>}
                            </button>
                        </div>
                    </div>

                    <div className="changePasswordAction">
                        <button className="submitPasswordBtn" onClick={handleChangePassword} disabled={isSubmitting}>
                            {isSubmitting ? "Updating..." : "Update Password"}
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default ChangePassword;